<template>
  <div class="comment-suggest">
    <component
      :is="settings.tag"
      ref="input"
      :value="modelValue"
      class="form-control"
      :class="{ 'is-invalid': invalid }"
      :rows="settings.tag === 'textarea' ? rows : null"
      :maxlength="settings.maxLength"
      :placeholder="placeholder"
      autocomplete="off"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="showList ? 'true' : 'false'"
      :aria-controls="listId"
      :aria-activedescendant="active >= 0 ? `${listId}-${active}` : null"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter="onEnter"
      @keydown.esc="onEsc"
    />
    <div v-if="showList" :id="listId" class="suggest-list" role="listbox">
      <div v-if="!query" class="suggest-head">{{ $t(settings.heading) }}</div>
      <!-- mousedown.prevent keeps focus (and the phone keyboard) on the field while picking -->
      <div
        v-for="(s, i) in matches"
        :id="`${listId}-${i}`"
        :key="s.text"
        class="suggest-item"
        :class="{ active: i === active }"
        role="option"
        :aria-selected="i === active ? 'true' : 'false'"
        @mousedown.prevent="pick(s.text)"
        @mouseenter="active = i"
      >
        <template v-for="(part, j) in s.parts" :key="j"><strong v-if="part.hit">{{ part.text }}</strong><template v-else>{{ part.text }}</template></template>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";

const FIELDS = {
  comment: { tag: "textarea", maxLength: 100, url: "comment-suggestions", heading: "endOfDay.recentDescriptions" },
  reference: { tag: "input", maxLength: 12, url: "reference-suggestions", heading: "endOfDay.recentReferences" },
};

// One fetch per field and user per page load (newest first, max 200 on the server); filtering happens
// locally while typing so suggestions appear instantly. Cleared after a save so new text shows up.
const cache = new Map();
export function clearCommentSuggestions() { cache.clear(); }
function loadSuggestions(field, userId) {
  const key = `${field}-${userId || ""}`;
  if (!cache.has(key)) {
    const q = userId ? `?userId=${userId}` : "";
    cache.set(key, api.get(`/Timesheet/${FIELDS[field].url}${q}`).then(r => r.data || []).catch(e => { console.error(e); cache.delete(key); return []; })); // eslint-disable-line no-console
  }
  return cache.get(key);
}

// Case- and accent-insensitive, one character in → one character out so matches map back for highlighting
function foldChar(ch) {
  const l = ch.toLowerCase();
  return l === "ð" ? "d" : (l.normalize("NFD")[0] || l);
}

let uid = 0;

export default {
  name: "CommentSuggestInput",
  props: {
    modelValue: { type: String, default: "" },
    userId: { type: String, default: null }, // admin registering for another user: that user's descriptions
    placeholder: { type: String, default: "" },
    invalid: { type: Boolean, default: false },
    rows: { type: Number, default: 2 },
    field: { type: String, default: "comment" }, // comment (Vinnulýsing) or reference (Tilvísun)
  },
  emits: ["update:modelValue"],
  data() {
    return { suggestions: [], open: false, active: -1, listId: `comment-suggest-${++uid}`, maxShown: 6, pointerDown: false, closePending: false };
  },
  computed: {
    settings() { return FIELDS[this.field]; },
    query() { return this.modelValue.trim(); },
    /** Every typed word must appear; descriptions starting with the text first, then word starts, then newest. */
    matches() {
      const chars = s => Array.from(s);
      const fold = s => chars(s).map(foldChar).join("");
      const terms = fold(this.query).split(/\s+/).filter(Boolean);
      const current = fold(this.query);
      const scored = [];
      this.suggestions.forEach((text, recency) => {
        const f = fold(text);
        if (f === current) return;
        if (!terms.every(t => f.includes(t))) return;
        const score = !terms.length ? 0 : f.startsWith(terms[0]) ? 0 : f.includes(" " + terms[0]) ? 1 : 2;
        scored.push({ text, f, score, recency });
      });
      scored.sort((a, b) => a.score - b.score || a.recency - b.recency);
      return scored.slice(0, this.maxShown).map(({ text, f }) => {
        const hit = new Array(f.length).fill(false);
        for (const t of terms) {
          const at = f.indexOf(t);
          for (let k = at; k >= 0 && k < at + t.length; k++) hit[k] = true;
        }
        const parts = [];
        chars(text).forEach((ch, k) => {
          const last = parts[parts.length - 1];
          if (last && last.hit === hit[k]) last.text += ch; else parts.push({ text: ch, hit: hit[k] });
        });
        return { text, parts };
      });
    },
    showList() { return this.open && this.matches.length > 0; },
  },
  watch: {
    matches() { if (this.active >= this.matches.length) this.active = -1; },
  },
  created() {
    // Room for more on a desktop screen; phones keep the list short above the keyboard
    this.maxShown = window.matchMedia && window.matchMedia("(min-width: 768px)").matches ? 8 : 5;
  },
  mounted() {
    document.addEventListener("pointerdown", this.onDocPointerDown, true);
    document.addEventListener("pointerup", this.onDocPointerUp, true);
  },
  beforeUnmount() {
    document.removeEventListener("pointerdown", this.onDocPointerDown, true);
    document.removeEventListener("pointerup", this.onDocPointerUp, true);
  },
  methods: {
    // The list sits in the page flow. Closing it while a press is in progress would move whatever was
    // pressed (e.g. Vista) away from under the pointer and swallow the click — so wait for the release.
    onDocPointerDown() { this.pointerDown = true; },
    onDocPointerUp() {
      this.pointerDown = false;
      if (!this.closePending) return;
      this.closePending = false;
      setTimeout(() => { this.open = false; }, 0); // after the click that follows this release
    },
    onBlur() {
      if (this.pointerDown) this.closePending = true; else this.open = false;
    },
    async onFocus() {
      this.open = true;
      this.active = -1;
      this.suggestions = await loadSuggestions(this.field, this.userId);
    },
    onInput(e) {
      this.$emit("update:modelValue", e.target.value);
      this.open = true;
      this.active = -1;
    },
    move(delta) {
      if (!this.showList) { this.open = true; return; }
      const n = this.matches.length;
      this.active = this.active < 0 ? (delta > 0 ? 0 : n - 1) : (this.active + delta + n) % n;
    },
    onEnter(e) {
      if (!this.showList || this.active < 0) return; // plain Enter = new line
      e.preventDefault();
      this.pick(this.matches[this.active].text);
    },
    onEsc(e) {
      if (!this.showList) return;
      e.stopPropagation(); // close the list, not the surrounding dialog
      this.open = false;
    },
    pick(text) {
      this.$emit("update:modelValue", text.slice(0, this.settings.maxLength)); // older descriptions can be longer
      this.open = false;
      this.active = -1;
      this.$nextTick(() => {
        const el = this.$refs.input;
        if (el) el.setSelectionRange(text.length, text.length);
      });
    },
  },
};
</script>

<style scoped>
.comment-suggest input { padding-left: 0.75rem !important; border: 1px solid #d2d6da !important; border-radius: 0.5rem; }
.comment-suggest .is-invalid { border-color: #ea0606 !important; box-shadow: none; }
.suggest-list { margin-top: 0.25rem; border: 1px solid #d2d6da; border-radius: 0.5rem; background: #fff; max-height: 15rem; overflow-y: auto; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06); }
.suggest-head { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #8392ab; padding: 0.45rem 0.75rem 0.2rem; }
.suggest-item { padding: 0.5rem 0.75rem; font-size: 0.875rem; color: #344767; cursor: pointer; line-height: 1.3; }
.suggest-item + .suggest-item { border-top: 1px solid #f0f2f5; }
.suggest-item.active, .suggest-item:hover { background: #f0f2f5; }
.suggest-item strong { color: #111; }
</style>
